import * as i0 from '@angular/core';
import { Injectable, EventEmitter, TemplateRef, HostListener, Output, Input, ContentChild, ViewChild, ViewEncapsulation, Component, NgModule } from '@angular/core';
import { E11TemplateMap, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import { BehaviorSubject } from 'rxjs';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { state, transition, style, group, animate, trigger } from '@angular/animations';
import { fadeOutActions, slideFromLeftActions, fadeInActions, slideFromRightActions, animationFadeInOut } from '@engineering11/ui-lib/e11-animations';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';

const OVERLAY_DEFAULT_CONFIG = { size: 'lg', background: true, sideOverPosition: 'right' };
class E11SideOverService {
    constructor() {
        // overlay on/off switch - defaults to on/true
        this.overlayStatusSource = new BehaviorSubject({ status: false, config: OVERLAY_DEFAULT_CONFIG });
        this.overlayStatusObservable$ = this.overlayStatusSource.asObservable();
    }
    overlayToggle(status, content, config) {
        const configToUse = { ...OVERLAY_DEFAULT_CONFIG, ...config };
        const value = { status, content, config: configToUse };
        if (content) {
            this.content = content;
        }
        this.overlayStatusSource.next(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });

/**
 * A lightweight wrapper around the side over service providing a simpler interface for the most common use case
 */
class E11SideOverContainerComponent extends E11TemplateMap {
    constructor(e11SideOverService) {
        super();
        this.e11SideOverService = e11SideOverService;
        this.size = 'md';
        this.background = true;
        this.sideOverPosition = 'right';
        this.closeOnEscape = true;
        this.closeOnClickOutside = false; // keep off
        this.sideOverContainerClosed = new EventEmitter();
    }
    onKeydownHandler(event) {
        if (this.closeOnEscape) {
            this.close();
        }
    }
    // ngAfterContentInit() {
    //   this.e11Templates.forEach((e11Template) => {
    //     this.e11TemplateMap[e11Template.getType()] = e11Template.template
    //   })
    // }
    open() {
        this.e11SideOverService.overlayToggle(true, this.sideOverContainer, {
            size: this.size,
            background: this.background,
            sideOverPosition: this.sideOverPosition,
        });
    }
    close() {
        this.sideOverContainerClosed.emit(true);
        this.e11SideOverService.overlayToggle(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverContainerComponent, deps: [{ token: E11SideOverService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11SideOverContainerComponent, selector: "e11-side-over-container", inputs: { containerClassOverrides: "containerClassOverrides", size: "size", background: "background", sideOverPosition: "sideOverPosition", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside" }, outputs: { sideOverContainerClosed: "sideOverContainerClosed" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)" } }, queries: [{ propertyName: "sideOverContentFallback", first: true, predicate: TemplateRef, descendants: true }], viewQueries: [{ propertyName: "sideOverContainer", first: true, predicate: ["sideOverContainer"], descendants: true }], usesInheritance: true, ngImport: i0, template: "<ng-template #sideOverContainer>\n  <div class=\"e11-side-over-container e11-bg-white e11-h-full {{ containerClassOverrides }}\">\n    <ng-container *ngTemplateOutlet=\"e11TemplateMap['content'] ?? sideOverContentFallback\"></ng-container>\n  </div>\n</ng-template>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-side-over-container', encapsulation: ViewEncapsulation.None, template: "<ng-template #sideOverContainer>\n  <div class=\"e11-side-over-container e11-bg-white e11-h-full {{ containerClassOverrides }}\">\n    <ng-container *ngTemplateOutlet=\"e11TemplateMap['content'] ?? sideOverContentFallback\"></ng-container>\n  </div>\n</ng-template>\n" }]
        }], ctorParameters: () => [{ type: E11SideOverService }], propDecorators: { sideOverContainer: [{
                type: ViewChild,
                args: ['sideOverContainer']
            }], sideOverContentFallback: [{
                type: ContentChild,
                args: [TemplateRef]
            }], containerClassOverrides: [{
                type: Input
            }], size: [{
                type: Input
            }], background: [{
                type: Input
            }], sideOverPosition: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], sideOverContainerClosed: [{
                type: Output
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }] } });

const animateFromLeft = [
    state('left', style({ left: '0' })),
    transition('left => *', [group([...fadeOutActions({ time: '0.4s' }), ...slideFromLeftActions({ start: '0', end: '-50%' })])]),
    transition('* => left', [
        group([...fadeInActions({ time: '0.2s' }), ...slideFromLeftActions(), style({ zIndex: '-1' }), animate('0s linear', style({ zIndex: '51' }))]),
    ]),
];
const animateFromRight = [
    state('right', style({ right: '0' })),
    transition('right => *', [group([...fadeOutActions({ time: '0.4s' }), ...slideFromRightActions({ start: '0', end: '-50%' })])]),
    transition('* => right', [
        group([...fadeInActions({ time: '0.2s' }), ...slideFromRightActions(), style({ zIndex: '-1' }), animate('0s linear', style({ zIndex: '51' }))]),
    ]),
];
const animationSideOver = trigger('animationSideOver', [...animateFromLeft, ...animateFromRight]);
class E11SideOverComponent {
    constructor(sideOverService) {
        this.sideOverService = sideOverService;
        this.closeOnEscape = true;
        this.closeOnClickOutside = false; // keep off
        this.sideOverClosed = new EventEmitter();
        this.overlayStatus = false;
        this.content = null;
        this.config = OVERLAY_DEFAULT_CONFIG;
        // Backdrop
        this.hasBackdrop = true;
        this.backdropStyle = 'dark';
        this.isBackdropOpen = false;
    }
    onKeydownHandler(event) {
        if (this.closeOnEscape) {
            this.close();
        }
    }
    ngOnInit() {
        this.sideOverService.overlayStatusObservable$.subscribe((res) => {
            this.overlayStatus = res.status;
            this.config = res.config;
            this.content = res.content ?? null;
            res.status ? (this.isBackdropOpen = true) : (this.isBackdropOpen = false);
        });
    }
    ngOnDestroy() {
        this.isBackdropOpen = false;
    }
    close() {
        this.sideOverClosed.emit(true);
        this.sideOverService.overlayToggle(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverComponent, deps: [{ token: E11SideOverService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11SideOverComponent, selector: "e11-side-over", inputs: { containerClass: "containerClass", backdropContainerClass: "backdropContainerClass", closeOnEscape: "closeOnEscape", closeOnClickOutside: "closeOnClickOutside", hasBackdrop: "hasBackdrop", backdropStyle: "backdropStyle" }, outputs: { sideOverClosed: "sideOverClosed" }, host: { listeners: { "document:keydown.escape": "onKeydownHandler($event)" } }, ngImport: i0, template: "<div\n  class=\"e11-side-over e11-fixed e11-overflow-hidden\"\n  aria-labelledby=\"slide-over-title\"\n  role=\"dialog\"\n  aria-modal=\"true\"\n  [ngClass]=\"{ 'e11-side-over-active': overlayStatus }\"\n>\n  <div class=\"e11-absolute e11-overflow-hidden\">\n    <div class=\"e11-absolute\" aria-hidden=\"true\">\n      <div\n        *ngIf=\"overlayStatus\"\n        class=\"e11-fixed e11-inset-y-0 e11-pl-10 e11-max-w-full e11-flex\"\n        [ngClass]=\"{\n          'e11-left-0': config.sideOverPosition === 'left',\n          'e11-right-0 e11-pl-10': config.sideOverPosition === 'right'\n        }\"\n        [@animationSideOver]=\"config.sideOverPosition\"\n      >\n        <div\n          [ngClass]=\"{\n            'e11-translate-x-0': overlayStatus,\n            'e11-translate-x-full': !overlayStatus\n          }\"\n          class=\"e11-side-over-container e11-side-over-{{ config.size }} e11-w-screen\n           {{ containerClass }}\"\n        >\n          <ng-container *ngTemplateOutlet=\"content\"></ng-container>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  *ngIf=\"hasBackdrop && isBackdropOpen\"\n  class=\"e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm e11-bg-black/70\"\n  [@animationFadeInOut]\n></div>\n", styles: [".e11-side-over{transition:z-index 0s linear .4s;z-index:-1}.e11-side-over .e11-side-over-container.e11-side-over-sm{width:24rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-md{width:32rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-lg{width:40rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-xl{width:56rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-full{width:98vw;max-width:100%}.e11-side-over.e11-side-over-active{transition:z-index 0s linear 0s;z-index:51}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }], animations: [animationFadeInOut(), animationSideOver] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-side-over', animations: [animationFadeInOut(), animationSideOver], template: "<div\n  class=\"e11-side-over e11-fixed e11-overflow-hidden\"\n  aria-labelledby=\"slide-over-title\"\n  role=\"dialog\"\n  aria-modal=\"true\"\n  [ngClass]=\"{ 'e11-side-over-active': overlayStatus }\"\n>\n  <div class=\"e11-absolute e11-overflow-hidden\">\n    <div class=\"e11-absolute\" aria-hidden=\"true\">\n      <div\n        *ngIf=\"overlayStatus\"\n        class=\"e11-fixed e11-inset-y-0 e11-pl-10 e11-max-w-full e11-flex\"\n        [ngClass]=\"{\n          'e11-left-0': config.sideOverPosition === 'left',\n          'e11-right-0 e11-pl-10': config.sideOverPosition === 'right'\n        }\"\n        [@animationSideOver]=\"config.sideOverPosition\"\n      >\n        <div\n          [ngClass]=\"{\n            'e11-translate-x-0': overlayStatus,\n            'e11-translate-x-full': !overlayStatus\n          }\"\n          class=\"e11-side-over-container e11-side-over-{{ config.size }} e11-w-screen\n           {{ containerClass }}\"\n        >\n          <ng-container *ngTemplateOutlet=\"content\"></ng-container>\n        </div>\n      </div>\n    </div>\n  </div>\n</div>\n\n<div\n  *ngIf=\"hasBackdrop && isBackdropOpen\"\n  class=\"e11-backdrop e11-flex e11-inset-0 e11-fixed e11-z-40 e11-backdrop-blur-sm e11-bg-black/70\"\n  [@animationFadeInOut]\n></div>\n", styles: [".e11-side-over{transition:z-index 0s linear .4s;z-index:-1}.e11-side-over .e11-side-over-container.e11-side-over-sm{width:24rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-md{width:32rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-lg{width:40rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-xl{width:56rem;max-width:100%}.e11-side-over .e11-side-over-container.e11-side-over-full{width:98vw;max-width:100%}.e11-side-over.e11-side-over-active{transition:z-index 0s linear 0s;z-index:51}\n"] }]
        }], ctorParameters: () => [{ type: E11SideOverService }], propDecorators: { containerClass: [{
                type: Input
            }], backdropContainerClass: [{
                type: Input
            }], closeOnEscape: [{
                type: Input
            }], closeOnClickOutside: [{
                type: Input
            }], sideOverClosed: [{
                type: Output
            }], hasBackdrop: [{
                type: Input
            }], backdropStyle: [{
                type: Input
            }], onKeydownHandler: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }] } });

class E11SideOverModule {
    static forRoot() {
        return {
            ngModule: E11SideOverModule,
            providers: [], // Be extremely careful about declaring services in providers arrays - it can prevent them from being singletons
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverModule, declarations: [E11SideOverComponent, E11SideOverContainerComponent], imports: [CommonModule, E11UtilModule, E11DirectivesModule, E11ButtonModule], exports: [E11SideOverComponent, E11SideOverContainerComponent, E11UtilModule, E11DirectivesModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverModule, imports: [CommonModule, E11UtilModule, E11DirectivesModule, E11ButtonModule, E11UtilModule, E11DirectivesModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11SideOverModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11SideOverComponent, E11SideOverContainerComponent],
                    imports: [CommonModule, E11UtilModule, E11DirectivesModule, E11ButtonModule],
                    exports: [E11SideOverComponent, E11SideOverContainerComponent, E11UtilModule, E11DirectivesModule],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11SideOverComponent, E11SideOverContainerComponent, E11SideOverModule, E11SideOverService, OVERLAY_DEFAULT_CONFIG };
